import type { AppSettings, FavoriteItem, SourceConfig } from "../types";

const SOURCES_KEY = "streamradar.sources.v2";
const SETTINGS_KEY = "streamradar.settings.v2";
const FAVORITES_KEY = "streamradar.favorites.v1";
const XTREAM_SESSION_PREFIX = "streamradar.xtream.session.";

export const defaultSettings: AppSettings = {
  theme: "dark",
  hideAdult: true,
  maxResults: 120,
  metadataLanguage: "pt-BR",
  metadataRegion: "BR",
  aliases: `# Um alias por linha\npriest=padre 2011,padre\nos 100=the 100,the hundred\nhome alone=esqueceram de mim\n`,
};

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
  } catch {
    return fallback;
  }
}

export function loadSources(): SourceConfig[] {
  try {
    const raw = localStorage.getItem(SOURCES_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveSources(sources: SourceConfig[]): void {
  // Mantém compatibilidade síncrona gravando em localStorage como backup
  try {
    localStorage.setItem(SOURCES_KEY, JSON.stringify(sources));
  } catch {}
  // Gravará também no IndexedDB de forma assíncrona (se disponível)
  (async () => {
    try {
      const dbReq = indexedDB.open("streamradar-db", 1);
      dbReq.onupgradeneeded = () => {
        dbReq.result.createObjectStore("config");
      };
      await new Promise((res, rej) => {
        dbReq.onsuccess = () => res(undefined);
        dbReq.onerror = () => rej(dbReq.error);
      });
      const db = dbReq.result;
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction("config", "readwrite");
        const store = tx.objectStore("config");
        const req = store.put(sources, SOURCES_KEY);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    } catch {
      /* ignore */
    }
  })();
}

export async function loadSourcesAsync(): Promise<SourceConfig[]> {
  try {
    // tenta ler do IndexedDB
    const dbReq = indexedDB.open("streamradar-db", 1);
    dbReq.onupgradeneeded = () => {
      dbReq.result.createObjectStore("config");
    };
    await new Promise((res, rej) => {
      dbReq.onsuccess = () => res(undefined);
      dbReq.onerror = () => rej(dbReq.error);
    });
    const db = dbReq.result;
    const idbVal = await new Promise<SourceConfig[] | undefined>((resolve) => {
      try {
        const tx = db.transaction("config", "readonly");
        const store = tx.objectStore("config");
        const req = store.get(SOURCES_KEY);
        req.onsuccess = () => resolve(req.result as SourceConfig[] | undefined);
        req.onerror = () => resolve(undefined);
      } catch {
        resolve(undefined);
      }
    });
    if (Array.isArray(idbVal)) return idbVal;

    // migra do localStorage se existir
    try {
      const raw = localStorage.getItem(SOURCES_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as SourceConfig[];
        // grava no idb para futura leitura
        const tx = db.transaction("config", "readwrite");
        tx.objectStore("config").put(parsed, SOURCES_KEY);
        return parsed;
      }
    } catch {
      /* ignore */
    }

    return [];
  } catch {
    return [];
  }
}

export function loadSettings(): AppSettings {
  return readJson(SETTINGS_KEY, defaultSettings);
}

export function saveSettings(settings: AppSettings): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

export function loadFavorites(): FavoriteItem[] {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((item) => item?.favoriteKey && item?.title)
      : [];
  } catch {
    return [];
  }
}

export function saveFavorites(favorites: FavoriteItem[]): void {
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
}

export function saveXtreamSession(sourceId: string, password: string): void {
  if (password)
    sessionStorage.setItem(`${XTREAM_SESSION_PREFIX}${sourceId}`, password);
  else sessionStorage.removeItem(`${XTREAM_SESSION_PREFIX}${sourceId}`);
}

export function loadXtreamSession(sourceId: string): string {
  return sessionStorage.getItem(`${XTREAM_SESSION_PREFIX}${sourceId}`) || "";
}

export function exportLocalConfig(
  sources: SourceConfig[],
  settings: AppSettings,
): void {
  const safeSources = sources.map(({ ...source }) => source);
  const blob = new Blob(
    [
      JSON.stringify(
        {
          version: 2,
          exportedAt: new Date().toISOString(),
          settings,
          sources: safeSources,
        },
        null,
        2,
      ),
    ],
    { type: "application/json" },
  );
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `streamradar-config-${new Date().toISOString().slice(0, 10)}.json`;
  link.click();
  URL.revokeObjectURL(link.href);
}
